import { produce } from 'immer'

import { INBOX_PAGE_LIMIT } from '@/reducers/tasks/tasks.constants'

// Action Types
export const SET_LOADING = 'promptFeedback/setLoading'
export const SET_ERROR = 'promptFeedback/setError'
export const SET_PROMPT_FEEDBACK_TASKS = 'promptFeedback/setPromptFeedbackTasks'
export const SET_PAGINATED_TASKS = 'promptFeedback/setPaginatedTasks'
export const SET_PAGINATION = 'promptFeedback/setPagination'
export const SET_FEEDBACK_OPTIMIZATIONS = 'promptFeedback/setFeedbackOptimizations'
export const SET_OPTIMIZATION_DETAIL = 'promptFeedback/setOptimizationDetail'
export const SET_OPTIMIZATION_DETAIL_LOADING = 'promptFeedback/setOptimizationDetailLoading'
export const SET_AGENT_AGENDA = 'promptFeedback/setAgentAgenda'
export const SET_AGENT_AGENDA_LOADING = 'promptFeedback/setAgentAgendaLoading'

// The Calibration Agent's agent id and verdicts, mirroring summary-service's calibration outcome. Run
// statuses are the promptimizer slice's RUN_STATUS — both read `optimizations.status`.
export const CALIBRATION_AGENT = 'qa_criterion'
export const CALIBRATION_RESULT = {
  NEEDS_REVIEW: 'needs_review',
  ALREADY_ADOPTED: 'already_adopted',
  NO_CANDIDATE: 'no_candidate',
  AUTO_APPLIED: 'auto_applied',
}
export const CALIBRATION_STOP = {
  SUCCESS: 'success',
  ALREADY_SATISFIED: 'corrections_already_satisfied',
  MAX_ITERATIONS: 'max_iterations',
  NO_GAIN: 'no_gain',
  COST_CEILING: 'cost_ceiling',
}
export const CALIBRATION_POLL_MS = 5000
// summary-service's limit on "Help the Agent" guidance (CalibrationRequest.guidance).
export const GUIDANCE_MAX_LENGTH = 2000

// Action Creators
export const setLoading = (payload) => ({ type: SET_LOADING, payload })
export const setError = (payload) => ({ type: SET_ERROR, payload })
export const setPromptFeedbackTasks = (payload) => ({ type: SET_PROMPT_FEEDBACK_TASKS, payload })
export const setPaginatedTasks = (payload) => ({ type: SET_PAGINATED_TASKS, payload })
export const setPagination = (payload) => ({ type: SET_PAGINATION, payload })
export const setFeedbackOptimizations = (payload) => ({
  type: SET_FEEDBACK_OPTIMIZATIONS,
  payload,
})
export const setOptimizationDetail = (payload) => ({ type: SET_OPTIMIZATION_DETAIL, payload })
export const setOptimizationDetailLoading = (payload) => ({
  type: SET_OPTIMIZATION_DETAIL_LOADING,
  payload,
})
export const setAgentAgenda = (payload) => ({ type: SET_AGENT_AGENDA, payload })
export const setAgentAgendaLoading = (payload) => ({ type: SET_AGENT_AGENDA_LOADING, payload })

// State
export const initialState = {
  tasks: [],
  loading: false,
  error: null,
  pagination: {
    page: 1,
    limit: INBOX_PAGE_LIMIT,
    totalCount: 0,
    totalPages: 0,
  },
  counts: {
    inbox: 0,
    notReady: 0,
    archive: 0,
  },
  // byTaskId is merged, not replaced, so paging the inbox doesn't drop earlier pages' chips.
  optimizations: {
    byTaskId: {},
    detail: {},
    detailLoading: false,
    agenda: [],
    agendaLoading: false,
  },
}

// Reducer
export default function promptFeedbackReducer(state = initialState, action) {
  return produce(state, (newState) => {
    const { payload } = action

    switch (action.type) {
      case SET_LOADING:
        newState.loading = payload
        break
      case SET_ERROR:
        newState.error = payload
        break
      case SET_PROMPT_FEEDBACK_TASKS:
        newState.tasks = payload
        break
      case SET_PAGINATED_TASKS:
        newState.tasks = payload.tasks
        newState.pagination = {
          page: payload.page,
          limit: payload.limit,
          totalCount: payload.total_count,
          totalPages: payload.total_pages,
        }
        newState.counts = {
          inbox: payload.inbox_count,
          notReady: payload.not_ready_count,
          archive: payload.archive_count,
        }
        break
      case SET_PAGINATION:
        newState.pagination = { ...newState.pagination, ...payload }
        break
      case SET_FEEDBACK_OPTIMIZATIONS:
        payload.forEach((optimization) => {
          newState.optimizations.byTaskId[optimization.task_id] = optimization
        })
        break
      case SET_OPTIMIZATION_DETAIL:
        newState.optimizations.detail[payload.optimization_id] = payload
        break
      case SET_OPTIMIZATION_DETAIL_LOADING:
        newState.optimizations.detailLoading = payload
        break
      case SET_AGENT_AGENDA:
        newState.optimizations.agenda = payload
        // The agenda carries each task's latest run — feed the card-chip map too so the two views agree.
        payload.forEach((item) => {
          if (item.optimization_id) {
            newState.optimizations.byTaskId[item.task_id] = item
          }
        })
        break
      case SET_AGENT_AGENDA_LOADING:
        newState.optimizations.agendaLoading = payload
        break
      default:
        break
    }
  })
}
